import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Home, ArrowLeft, Search, HelpCircle } from 'lucide-react';
import SEO from '@/hooks/useSEO';

const NotFound = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    console.error(
      '404 Error: User attempted to access non-existent route:',
      location.pathname
    );
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-6">
      <SEO
        title="Page Not Found"
        description="The page you are looking for does not exist."
        noindex
      />
      <div className="container max-w-2xl mx-auto">
        <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-2xl">
          <CardContent className="p-12 text-center space-y-8">
            {/* Large 404 Number */}
            <div className="space-y-4">
              <div className="text-8xl md:text-9xl font-bold text-learning-primary/20 leading-none">
                404
              </div>
              <HelpCircle className="h-16 w-16 mx-auto text-learning-primary/40" />
            </div>

            {/* Error Message */}
            <div className="space-y-4">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground">
                {t('notFound.title')}
              </h1>
              <p className="text-xl text-muted-foreground max-w-md mx-auto">
                {t('notFound.message')}
              </p>
              <div className="text-sm text-muted-foreground font-mono bg-muted/50 px-4 py-2 rounded border">
                {location.pathname}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
              <Button
                onClick={() => navigate('/')}
                size="lg"
                className="bg-learning-primary hover:bg-learning-primary/90"
              >
                <Home className="h-5 w-5 mr-2" />
                {t('notFound.returnHome')}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/questions')}
                size="lg"
                className="bg-white/50 backdrop-blur-sm"
              >
                <Search className="h-5 w-5 mr-2" />
                {t('questions.browseQuestions')}
              </Button>
            </div>

            {/* Helpful Links */}
            <div className="pt-8 border-t border-border/20">
              <p className="text-sm text-muted-foreground mb-4">
                Looking for something specific?
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/articles')}
                  className="text-learning-primary hover:text-learning-primary/80"
                >
                  Articles
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/questions')}
                  className="text-learning-primary hover:text-learning-primary/80"
                >
                  Question Catalogs
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/dashboard')}
                  className="text-learning-primary hover:text-learning-primary/80"
                >
                  Dashboard
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/settings')}
                  className="text-learning-primary hover:text-learning-primary/80"
                >
                  Settings
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NotFound;
